/**
 * Refresh-on-boot helper for AuthProvider (SE-5105).
 *
 * SE-5107 shipped the symptom fix — signing the user out when an expired
 * access token is found in `@authData` at boot. This module is the
 * deferred root-cause fix: use the still-valid refresh token to silently
 * mint a new access token so the user never sees `/login`.
 *
 * Implementation notes:
 *
 * - The refresh request is issued via raw `fetch`, NOT through Apollo
 *   Client. Reasons:
 *   (a) AuthProvider runs above ApolloClientProvider in the tree, so the
 *       Apollo client is not yet built when the hydration effect fires.
 *   (b) Routing this through Apollo would re-introduce the circular
 *       "auth-link awaits refresh awaits auth-link" hazard that broke
 *       `/knowledge-graph` ~4 months ago — the regression we are
 *       explicitly told not to recreate.
 *
 * - The fetch is bounded by REFRESH_ON_BOOT_TIMEOUT_MS (15s). A boot that
 *   hangs longer than that resolves to `null`, which the caller translates
 *   into a clean sign-out via `initializeAuthData(null)` rather than
 *   leaving the app in an infinite loading state.
 *
 * - Every failure mode (revoked refresh token, network error, malformed
 *   payload, timeout) resolves to `null`. The function never throws — the
 *   caller's contract is "give me a fresh `{accessToken, exp}` or tell me
 *   to log the user out", and we keep that boundary tight.
 * @module features/auth/providers/refreshOnBoot
 */

/**
 * Hard cap on how long the boot-time refresh fetch is allowed to take.
 *
 * SE-5105 — chosen to match the un-batched single-operation timeout used by
 * the GraphQL link (SE-5055). A boot can't afford to wait longer than a
 * normal request would; a hung refresh blocks the entire app.
 */
export const REFRESH_ON_BOOT_TIMEOUT_MS = 15_000;

/**
 * Arguments accepted by performRefreshOnBoot.
 */
interface PerformRefreshOnBootArgs {
  /** The refresh token persisted in `@authData`. Empty / undefined short-circuits to null. */
  refreshToken: string | null | undefined;
  /** GraphQL endpoint to POST the AuthRefreshToken mutation to. */
  graphqlUrl: string;
  /** Optional override for the timeout — tests use the default; callers should leave unset. */
  timeoutMs?: number;
  /**
   * Optional override for the fetch implementation. Tests pass `globalThis.fetch`
   * (which they jest.spyOn). Production code leaves this unset to use the global.
   */
  fetchImpl?: typeof fetch;
}

/**
 * Successful refresh-on-boot result — a fresh access token and the exp claim
 * extracted from its payload.
 */
export interface RefreshOnBootResult {
  /** The new JWT minted by the AuthRefreshToken mutation. */
  accessToken: string;
  /** Unix-seconds exp claim parsed out of the new JWT. */
  exp: number | undefined;
}

/**
 * Extracts the `exp` claim from a JWT's payload.
 *
 * Duplicated (intentionally, with the existing helper of the same name in
 * providers/ApolloClient.tsx) so this module has zero coupling to ApolloClient —
 * AuthProvider must not pull ApolloClient transitively, since AuthProvider
 * sits above ApolloClientProvider in the tree.
 * @param token - The JWT access token string.
 * @returns The exp claim from the token payload, or undefined if parsing fails.
 */
export function getExpFromToken(
  token: string | null | undefined
): number | undefined {
  if (token === undefined || token === null) return undefined;
  if (typeof token !== "string" || token === "") return undefined;

  try {
    const parts = token.split(".");
    if (parts.length !== 3) return undefined;
    const payload = parts[1];
    const decoded =
      typeof atob !== "undefined"
        ? atob(payload)
        : Buffer.from(payload, "base64").toString();
    const parsed = JSON.parse(decoded);
    return typeof parsed.exp === "number" ? parsed.exp : undefined;
  } catch {
    return undefined;
  }
}

/**
 * The raw GraphQL document for the AuthRefreshToken mutation.
 *
 * Mirrors the body of `AuthRefreshTokenDocument` in `generated/graphql.ts` but
 * inlined as a string so this module does not import anything Apollo-shaped.
 * The fragment is expanded inline to keep the request self-contained — the
 * server returns whatever subset of these fields it knows about.
 */
const AUTH_REFRESH_TOKEN_MUTATION = `mutation AuthRefreshToken($input: RefreshTokenInput!) {
  refreshToken(input: $input) {
    message
    data {
      accessToken
      expiresIn
      tokenType
      refreshToken
      idToken
    }
  }
}`;

/**
 * Shape of the AuthRefreshToken response we care about. Everything else is
 * tolerated and ignored.
 */
interface AuthRefreshTokenResponse {
  data?: {
    refreshToken?: {
      data?: {
        accessToken?: string | null;
      } | null;
    } | null;
  } | null;
  errors?: readonly {
    message?: string;
    extensions?: { code?: string } | null;
  }[];
}

/**
 * Issues the AuthRefreshToken mutation via raw fetch with a hard timeout.
 *
 * Resolves to `{ accessToken, exp }` on success, or `null` when the refresh
 * token is missing, the response was unauthorized, the response is
 * malformed, the network failed, or the timeout elapsed.
 *
 * Never throws — every failure mode is collapsed to `null` so the caller
 * can branch on a single boolean.
 * @param args - Refresh request configuration.
 * @returns A promise resolving to the new access-token bundle, or null.
 */
export async function performRefreshOnBoot(
  args: PerformRefreshOnBootArgs
): Promise<RefreshOnBootResult | null> {
  const {
    refreshToken,
    graphqlUrl,
    timeoutMs = REFRESH_ON_BOOT_TIMEOUT_MS,
    fetchImpl = globalThis.fetch,
  } = args;

  if (!refreshToken) return null;

  // Race a timeout against the fetch. `Promise.race` resolves with whichever
  // settles first; on timeout we also AbortController-cancel the fetch so the
  // socket isn't left dangling.
  const controller = new AbortController();
  // Stash the timer in a single-cell holder so we can clear it after the race
  // without reassigning a `let` (functional/no-let).
  const timeoutHandleRef: {
    current: ReturnType<typeof setTimeout> | undefined;
  } = {
    current: undefined,
  };

  const timeoutPromise = new Promise<null>(resolve => {
    timeoutHandleRef.current = setTimeout(() => {
      controller.abort();
      resolve(null);
    }, timeoutMs);
  });

  const fetchPromise = (async (): Promise<RefreshOnBootResult | null> => {
    try {
      const response = await fetchImpl(graphqlUrl, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          operationName: "AuthRefreshToken",
          query: AUTH_REFRESH_TOKEN_MUTATION,
          variables: { input: { refreshToken } },
        }),
        signal: controller.signal,
      });

      // 401 / 403 / 5xx — treat as revoked. Server already declined; don't
      // try to parse JSON that may not exist.
      if (!response.ok) return null;

      const body = (await response.json()) as AuthRefreshTokenResponse;

      // GraphQL-layer auth failure surfaces here, not at the HTTP layer.
      if (body.errors && body.errors.length > 0) return null;

      const accessToken = body.data?.refreshToken?.data?.accessToken;
      if (!accessToken) return null;

      return { accessToken, exp: getExpFromToken(accessToken) };
    } catch {
      // Network error, AbortError from timeout, malformed JSON — all funnel
      // to the same "sign the user out" outcome.
      return null;
    }
  })();

  try {
    return await Promise.race([fetchPromise, timeoutPromise]);
  } finally {
    if (timeoutHandleRef.current !== undefined)
      clearTimeout(timeoutHandleRef.current);
  }
}
